import React, { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import { ComposableMap, Geographies, Geography, Marker } from "react-simple-maps";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import autoTable from 'jspdf-autotable';
import Stack from '@mui/material/Stack';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableContainer from '@mui/material/TableContainer';
import TableSortLabel from '@mui/material/TableSortLabel';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Link from '@mui/material/Link';

const fieldProps = { size: 'medium', fullWidth: true };

function BuildInventory() {
  const [inventory, setInventory] = useState([]);
  const [newRecord, setNewRecord] = useState({
    dell_service_tag: '',
    software_type: '',
    status: '',
    customer_name: null,
    customer_address: null,
    ship_date: null,
    windows_license_key: ''
  });

  const [editingRecord, setEditingRecord] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSoftwareType, setFilterSoftwareType] = useState('');
  const [sortField, setSortField] = useState('');
  const [sortAsc, setSortAsc] = useState(true);
  const [graphData, setGraphData] = useState([]);
  const [showTrackingModal, setShowTrackingModal] = useState(false);
  const [selectedTrackingRecord, setSelectedTrackingRecord] = useState(null);
  const [editTrackingNumber, setEditTrackingNumber] = useState('');
  const [editTrackingUrl, setEditTrackingUrl] = useState('');
  const [isEditingTracking, setIsEditingTracking] = useState(false);
  const [filterStatus, setFilterStatus] = useState('');
  const [justAddedId, setJustAddedId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [showReportModal, setShowReportModal] = useState(false);
  const [detailRecord, setDetailRecord] = useState(null);
  const [reportMonth, setReportMonth] = useState('');
  const [reportYear, setReportYear] = useState('');

  const [metrics, setMetrics] = useState({
  cirrusProOnline: 0,
  shippedThisMonth: 0,
  shippedLastMonth: 0,
  readyToBuild: 0
});

  useEffect(() => {
    fetchInventory();
  }, []);

  useEffect(() => {
  setCurrentPage(1);
}, [searchTerm, filterStatus, filterSoftwareType]);

  const fetchInventory = async () => {
  const { data, error } = await supabase.from('build_inventory').select('*');
  if (!error) {
    setInventory(data);

    const onlineDevices = data.filter(d => d.status === 'Online');
    const classicCount = onlineDevices.filter(d => d.software_type === 'Classic').length;
    const cirrusCount = onlineDevices.filter(d => d.software_type === 'CirrusPro').length;
    setGraphData([
      { name: 'Classic', count: classicCount },
      { name: 'CirrusPro', count: cirrusCount }
    ]);

      const now = new Date();
      const currentMonth = now.getMonth();
      const currentYear = now.getFullYear();

      const cirrusProOnline = data.filter(
        d => d.software_type === 'CirrusPro' && d.status === 'Online'
      ).length;

      const shippedThisMonth = data.filter(d => {
        const shipDate = d.ship_date ? new Date(d.ship_date) : null;
        return d.status === 'Shipped' &&
          shipDate &&
          shipDate.getMonth() === currentMonth &&
          shipDate.getFullYear() === currentYear;
      }).length;

      const shippedLastMonth = data.filter(d => {
        const shipDate = d.ship_date ? new Date(d.ship_date) : null;
        const lastMonth = currentMonth === 0 ? 11 : currentMonth - 1;
        const lastMonthYear = currentMonth === 0 ? currentYear - 1 : currentYear;
        return d.status === 'Shipped' &&
          shipDate &&
          shipDate.getMonth() === lastMonth &&
          shipDate.getFullYear() === lastMonthYear;
      }).length;

      const readyToBuild = data.filter(d => d.status === 'Not Built').length;

      setMetrics({
        cirrusProOnline,
        shippedThisMonth,
        shippedLastMonth,
        readyToBuild
      });

      }
    };


  const getStatusColor = (status) => {
  switch (status) {
    case 'Online':
      return {
        background: '#e0f7e9',
        text: '#0b8e45',
        dot: '#0b8e45'
      };
    case 'Shipped':
      return {
        background: '#e6f4ff',
        text: '#0077cc',
        dot: '#0077cc'
      };
    case 'Not Built':
      return {
        background: '#fff4e5',
        text: '#d97706',
        dot: '#d97706'
      };
    default:
      return {
        background: '#f0f0f0',
        text: '#666',
        dot: '#999'
      };
  }
};

  const handleNewRecordChange = (e) => {
    const { name, value, type, checked } = e.target;
    setNewRecord({ ...newRecord, [name]: type === 'checkbox' ? checked : value });
  };

  const handleAddRecord = async () => {
  const preparedRecord = {
    ...newRecord,
    status: 'Not Built',
    ship_date: newRecord.ship_date || null
  };
  const { data, error } = await supabase.from('build_inventory').insert([preparedRecord]).select();
if (error) {
  toast.error('Error adding record: ' + error.message);
} else {
  toast.success('Record added!');
  setJustAddedId(data[0].dell_service_tag); // mark the new row
  setInventory(prev => [data[0], ...prev]);
  setNewRecord({
      dell_service_tag: '',
      software_type: '',
      status: '',
      windows_license_key: '',
      customer_name: '',
      customer_address: '',
      ship_date: newRecord.ship_date || null
    });
  }
};

  const handleSaveEdit = async () => {
  const cleanedShipDate = editingRecord.ship_date?.trim() === '' ? null : editingRecord.ship_date;

  const { error } = await supabase.from('build_inventory').update({
    software_type: editingRecord.software_type,
    status: editingRecord.status,
    customer_name: editingRecord.customer_name,
    customer_address: editingRecord.customer_address,
    ship_date: cleanedShipDate || null,
    windows_license_key: editingRecord.windows_license_key
  }).eq('dell_service_tag', editingRecord.dell_service_tag);

  if (error) {
    toast.error('Error updating record: ' + error.message);
  } else {
    toast.success('Record updated!');
    fetchInventory();
    setEditingRecord(null);

  }
};

  const handleClearFilters = () => {
    setSearchTerm('');
    setFilterStatus('');
    setFilterSoftwareType('');
  };

 const generatePDFReport = async (monthInput, yearInput) => {
  const year = parseInt(yearInput, 10);
  const month = parseInt(monthInput, 10);

  if (isNaN(year)) {
    toast.error("Please enter a valid year.");
    return;
  }

  const filtered = inventory.filter(item => {
    if (item.status !== 'Shipped' || !item.ship_date) return false;
    const date = new Date(item.ship_date);
    const shippedYear = date.getFullYear();
    const shippedMonth = date.getMonth() + 1;
    return shippedYear === year && (isNaN(month) || shippedMonth === month);
  })
.sort((a, b) => new Date(b.ship_date) - new Date(a.ship_date));

  if (filtered.length === 0) {
    toast.info('No shipped records found for that period.');
    return;
  }

  const doc = new jsPDF({ orientation: 'portrait' });

  const rows = filtered.map(item => [
    item.dell_service_tag,
    item.software_type,
    item.customer_name || '',
    item.customer_address || '',
    item.ship_date
  ? (() => {
      const [year, month, day] = item.ship_date.split('-');
      return `${month}/${day}/${year}`;
    })()
  : '',
    { content: '', link: item.tracking_url || '' }
  ]);

  const reportTitle = `PC Shipping Report - ${
    isNaN(month)
      ? `Year ${year}`
      : `${new Date(year, month - 1).toLocaleString('default', { month: 'long' })} ${year}`
  }`;

  doc.setFontSize(16);
  doc.text(reportTitle, 14, 20);
  doc.setFontSize(12);
  doc.setTextColor(100);
  doc.text(`Shipped Devices: ${rows.length}`, 14, 28);

  autoTable(doc, {
    startY: 34,
    head: [['Serial #', 'Software', 'Customer Name', 'Address', 'Ship Date', 'Tracking URL']],
    body: rows,
    styles: {
      fontSize: 7,
      cellPadding: 1.5,
    },
    headStyles: {
      fillColor: [0, 122, 51],
      textColor: 255,
      fontSize: 8
    },
    columnStyles: {
      3: { cellWidth: 45 },
      4: { cellWidth: 25 },
      5: { cellWidth: 35, halign: 'center', valign: 'top' }
    },
    margin: { left: 14, right: 14 },
    didDrawCell: (data) => {
      if (
        data.column.index === 5 &&
        typeof data.cell.raw === 'object' &&
        data.cell.raw?.link
      ) {
        const padding = 1;
        const fontSize = 7;
        const lineHeight = fontSize * 0.35;
        const x = data.cell.x + 2;
        const y = data.cell.y + padding + lineHeight;

        doc.setTextColor(0, 0, 255);
        doc.setFontSize(fontSize);
        doc.textWithLink('Tracking Link', x, y, { url: data.cell.raw.link });
        doc.setTextColor(0);
      }
    },
    didDrawPage: (data) => {
      const pageSize = doc.internal.pageSize;
      const pageHeight = pageSize.height || doc.internal.pageSize.getHeight();
      const pageNumber = doc.internal.getNumberOfPages();

      doc.setFontSize(8);
      doc.setTextColor(150);
      doc.text(`Generated on ${new Date().toLocaleDateString()}`, 14, pageHeight - 10);
      doc.text(`Page ${pageNumber}`, pageSize.width - 30, pageHeight - 10);
    }
  });

  const fileName = `shipping_report_${year}_${isNaN(month) ? 'yearly' : String(month).padStart(2, '0')}.pdf`;

  doc.save(fileName);

  const pdfBlob = doc.output('blob');
  const blobUrl = URL.createObjectURL(pdfBlob);
  window.open(blobUrl, '_blank');
  };

  const handleDeleteRecord = async () => {
  if (!editingRecord) return;
  const confirmDelete = window.confirm("Are you sure you want to delete this record?");
  if (!confirmDelete) return;

  const { error } = await supabase
    .from('build_inventory')
    .delete()
    .eq('dell_service_tag', editingRecord.dell_service_tag);

  if (error) {
    toast.error('Error deleting record: ' + error.message);
  } else {
    toast.success('Record deleted!');
    fetchInventory();
    setEditingRecord(null);
  }
};

  const filteredSortedInventory = [...inventory]
  .filter(item => {
    const textMatch = Object.values(item).some(value => value?.toString().toLowerCase().includes(searchTerm.toLowerCase()));
    const statusMatch = filterStatus ? (item.status?.toLowerCase() === filterStatus.toLowerCase()) : true;
    const softwareMatch = filterSoftwareType ? (item.software_type?.toLowerCase() === filterSoftwareType.toLowerCase()) : true;
    return textMatch && statusMatch && softwareMatch;
  })
  .sort((a, b) => {
  if (a.dell_service_tag === justAddedId) return -1;
  if (b.dell_service_tag === justAddedId) return 1;
    if (sortField) {
      if (sortField === 'status') {
        const order = { 'Not Built': 0, 'Online': 1, 'Shipped': 2 };
        return sortAsc
          ? (order[a.status] ?? 99) - (order[b.status] ?? 99)
          : (order[b.status] ?? 99) - (order[a.status] ?? 99);
      }
      const valA = a[sortField] ?? '';
      const valB = b[sortField] ?? '';

      if (sortField === 'ship_date') {
        return sortAsc
          ? new Date(valA || 0) - new Date(valB || 0)
          : new Date(valB || 0) - new Date(valA || 0);
      }

      if (typeof valA === 'boolean' && typeof valB === 'boolean') {
        return sortAsc ? valA - valB : valB - valA;
      }

      return sortAsc
        ? String(valA).localeCompare(String(valB))
        : String(valB).localeCompare(String(valA));
    } else {
      const statusOrder = { 'Online': 0, 'Shipped': 1, 'Not Built': 2 };
      const softwareOrder = { 'CirrusPro': 0, 'Classic': 1 };


const getWeight = (item) => {
  const statusWeight = statusOrder[item.status] ?? 99;
  const softwareWeight = softwareOrder[item.software_type] ?? 99;
  return statusWeight * 10 + softwareWeight;
};

const weightA = getWeight(a);
const weightB = getWeight(b);

// First, sort by weight
if (weightA !== weightB) {
  return weightA - weightB;
}

// Then, if both are Shipped, sort by ship_date DESC
if (a.status === 'Shipped' && b.status === 'Shipped') {
  const dateA = a.ship_date ? new Date(a.ship_date) : new Date(0);
  const dateB = b.ship_date ? new Date(b.ship_date) : new Date(0);
  return dateB - dateA; // latest date first
}

return 0;
    }
  });

  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedInventory = filteredSortedInventory.slice(startIndex, startIndex + itemsPerPage);

const exportToCSV = async () => {
  try {
    const dataToExport = filteredSortedInventory;

    if (dataToExport.length === 0) {
      toast.info('No records to export.');
      return;
    }

    const excludedFields = ['build_id', 'windows_license_key'];
    const headers = Object.keys(dataToExport[0]).filter(key => !excludedFields.includes(key));
    const csvRows = [];

    csvRows.push(headers.join(','));

    for (const row of dataToExport) {
      const processed = headers.map(key => {
        let val = row[key] ?? '';
        val = val.toString().replace(/\r?\n|\r/g, ' ');

        if (/^[=+\-#]/.test(val)) {
          val = `'${val}`;
        }

        val = val.replace(/"/g, '""');
        return `"${val}"`;
      });

      csvRows.push(processed.join(','));
    }

    const csvContent = '\uFEFF' + csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'build_inventory_export.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success(`Exported ${dataToExport.length} records.`);
  } catch (err) {
    console.error(err);
    toast.error('Unexpected error during export.');
  }
};


  return (
    <Stack spacing={3}>
      <ToastContainer position="top-right" autoClose={3000} />
      <Typography variant="h5" component="h2" fontWeight={700}>
        Build inventory
      </Typography>

      <Grid container spacing={2} alignItems="flex-start">
        <Grid size={{ xs: 12, md: 7 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
              Online devices
            </Typography>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={graphData}>
                <XAxis dataKey="name" />
                <YAxis allowDecimals={false} />
                <Tooltip
                  formatter={(value) => [value, 'Built and Online']}
                  cursor={{ fill: 'transparent' }}
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: 'none',
                    boxShadow: 'none'
                  }}
                />
                <Bar
                  dataKey="count"
                  name="Built and Online"
                  fill="#fc7b03"
                  isAnimationActive={false}
                  activeBar={(props) => (
                    <rect
                      x={props.x}
                      y={props.y}
                      width={props.width}
                      height={props.height}
                      fill="#fc7b03"
                    />
                  )}
                />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 5 }}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
              Information
            </Typography>
            <Stack spacing={0.5}>
              <Typography variant="body2"><strong>CirrusPro devices online:</strong> {metrics.cirrusProOnline}</Typography>
              <Typography variant="body2"><strong>Devices shipped this month:</strong> {metrics.shippedThisMonth}</Typography>
              <Typography variant="body2"><strong>Devices shipped last month:</strong> {metrics.shippedLastMonth}</Typography>
              <Typography variant="body2"><strong>Devices in stock, ready to build:</strong> {metrics.readyToBuild}</Typography>
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
          Add a new device
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'flex-start' }}>
          <TextField
            {...fieldProps}
            name="dell_service_tag"
            value={newRecord.dell_service_tag}
            onChange={handleNewRecordChange}
            label="Dell service tag"
            sx={{ flex: 1, maxWidth: { sm: 360 } }}
          />
          <Button variant="contained" onClick={handleAddRecord} sx={{ alignSelf: { xs: 'stretch', sm: 'center' }, minWidth: 100 }}>
            Add
          </Button>
        </Stack>
      </Paper>

      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
          Filters
        </Typography>
        <Stack spacing={2}>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                {...fieldProps}
                placeholder="Search…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                label="Search"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                {...fieldProps}
                select
                label="Software type"
                value={filterSoftwareType}
                onChange={(e) => setFilterSoftwareType(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="CirrusPro">CirrusPro</MenuItem>
                <MenuItem value="Classic">Classic</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                {...fieldProps}
                select
                label="Status"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="Online">Online</MenuItem>
                <MenuItem value="Shipped">Shipped</MenuItem>
                <MenuItem value="Not Built">Not Built</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Button variant="outlined" onClick={handleClearFilters}>
                  Clear filters
                </Button>
                <Button variant="outlined" onClick={() => setShowReportModal(true)}>
                  Generate shipping report
                </Button>
                <Button variant="outlined" onClick={exportToCSV}>
                  Export CSV
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Stack>
      </Paper>
      {editingRecord && (
        <Paper sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography variant="subtitle1" fontWeight={700} gutterBottom>
            Editing record: {editingRecord.dell_service_tag}
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                {...fieldProps}
                select
                label="Software type"
                name="software_type"
                value={editingRecord.software_type || ''}
                onChange={(e) => setEditingRecord({ ...editingRecord, software_type: e.target.value })}
              >
                <MenuItem value="">Select software type</MenuItem>
                <MenuItem value="CirrusPro">CirrusPro</MenuItem>
                <MenuItem value="Classic">Classic</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                {...fieldProps}
                select
                label="Status"
                name="status"
                value={editingRecord.status || ''}
                onChange={(e) => setEditingRecord({ ...editingRecord, status: e.target.value })}
              >
                <MenuItem value="">Select status</MenuItem>
                <MenuItem value="Not Built">Not Built</MenuItem>
                <MenuItem value="Online">Online</MenuItem>
                <MenuItem value="Shipped">Shipped</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                {...fieldProps}
                name="customer_name"
                label="Customer name"
                value={editingRecord.customer_name || ''}
                onChange={(e) => setEditingRecord({ ...editingRecord, customer_name: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                {...fieldProps}
                name="customer_address"
                label="Customer address"
                value={editingRecord.customer_address || ''}
                onChange={(e) => setEditingRecord({ ...editingRecord, customer_address: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                {...fieldProps}
                name="windows_license_key"
                label="Windows license key"
                value={editingRecord.windows_license_key || ''}
                onChange={(e) => setEditingRecord({ ...editingRecord, windows_license_key: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                {...fieldProps}
                type="date"
                name="ship_date"
                label="Ship date"
                value={editingRecord.ship_date || ''}
                onChange={(e) => setEditingRecord({ ...editingRecord, ship_date: e.target.value })}
                slotProps={{ inputLabel: { shrink: true } }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Button variant="contained" onClick={handleSaveEdit}>Save</Button>
                <Button variant="outlined" onClick={() => setEditingRecord(null)}>Cancel</Button>
                <Button color="error" variant="outlined" onClick={handleDeleteRecord}>Delete</Button>
              </Stack>
            </Grid>
          </Grid>
        </Paper>
      )}

      <Paper
        sx={{
          p: 0,
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2
        }}
      >
        <Box
          sx={{
            px: { xs: 2, sm: 3 },
            py: 1.75,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            flexWrap: 'wrap',
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: (theme) =>
              theme.palette.mode === 'light' ? 'rgba(15, 23, 42, 0.03)' : 'action.hover'
          }}
        >
          <Typography variant="subtitle1" fontWeight={700}>
            Device inventory
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
            {filteredSortedInventory.length} device{filteredSortedInventory.length === 1 ? '' : 's'}
            {filteredSortedInventory.length !== inventory.length ? ` (filtered from ${inventory.length})` : ''}
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
              {' · '}
              Row opens details
            </Box>
          </Typography>
        </Box>
        <TableContainer sx={{ maxWidth: '100%' }}>
          <Table
            stickyHeader
            size="small"
            sx={{
              minWidth: 640,
              '& .MuiTableCell-root': {
                borderColor: 'divider'
              }
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell
                  padding="checkbox"
                  sx={{
                    width: 48,
                    bgcolor: 'background.paper',
                    py: 1.25,
                    borderBottomWidth: 2
                  }}
                />
                <TableCell
                  sx={{
                    fontWeight: 600,
                    fontSize: '0.7rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                    color: 'text.secondary',
                    bgcolor: 'background.paper',
                    py: 1.25,
                    borderBottomWidth: 2,
                    whiteSpace: 'nowrap'
                  }}
                >
                  Serial #
                </TableCell>
                <TableCell
                  sortDirection={sortField === 'status' ? (sortAsc ? 'asc' : 'desc') : false}
                  sx={{
                    fontWeight: 600,
                    fontSize: '0.7rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                    color: 'text.secondary',
                    bgcolor: 'background.paper',
                    py: 1.25,
                    borderBottomWidth: 2,
                    whiteSpace: 'nowrap'
                  }}
                >
                  <TableSortLabel
                    active={sortField === 'status'}
                    direction={sortField === 'status' ? (sortAsc ? 'asc' : 'desc') : 'asc'}
                    onClick={() => {
                      setSortField('status');
                      setSortAsc(sortField === 'status' ? !sortAsc : true);
                    }}
                    sx={{
                      '&.MuiTableSortLabel-root': { color: 'text.secondary' },
                      '&.MuiTableSortLabel-root.Mui-active': { color: 'text.primary' },
                      '& .MuiTableSortLabel-icon': { opacity: sortField === 'status' ? 1 : 0.35 }
                    }}
                  >
                    Status
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    fontSize: '0.7rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                    color: 'text.secondary',
                    bgcolor: 'background.paper',
                    py: 1.25,
                    borderBottomWidth: 2,
                    whiteSpace: 'nowrap'
                  }}
                >
                  Customer
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    fontSize: '0.7rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                    color: 'text.secondary',
                    bgcolor: 'background.paper',
                    py: 1.25,
                    borderBottomWidth: 2,
                    whiteSpace: 'nowrap'
                  }}
                >
                  Address
                </TableCell>
                <TableCell
                  sortDirection={sortField === 'ship_date' ? (sortAsc ? 'asc' : 'desc') : false}
                  sx={{
                    fontWeight: 600,
                    fontSize: '0.7rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                    color: 'text.secondary',
                    bgcolor: 'background.paper',
                    py: 1.25,
                    borderBottomWidth: 2,
                    whiteSpace: 'nowrap'
                  }}
                >
                  <TableSortLabel
                    active={sortField === 'ship_date'}
                    direction={sortField === 'ship_date' ? (sortAsc ? 'asc' : 'desc') : 'asc'}
                    onClick={() => {
                      setSortField('ship_date');
                      setSortAsc(sortField === 'ship_date' ? !sortAsc : true);
                    }}
                    sx={{
                      '&.MuiTableSortLabel-root': { color: 'text.secondary' },
                      '&.MuiTableSortLabel-root.Mui-active': { color: 'text.primary' },
                      '& .MuiTableSortLabel-icon': { opacity: sortField === 'ship_date' ? 1 : 0.35 }
                    }}
                  >
                    Ship date
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedInventory.map((item) => {
                const isSelected = editingRecord?.dell_service_tag === item.dell_service_tag;
                const isNew = item.dell_service_tag === justAddedId;
                const statusColors = getStatusColor(item.status);
                return (
                  <TableRow
                    key={item.dell_service_tag}
                    hover
                    selected={isSelected}
                    onClick={() => setDetailRecord(item)}
                    sx={{
                      cursor: 'pointer',
                      transition: 'background-color 0.15s ease',
                      ...(isNew && {
                        bgcolor: (theme) =>
                          theme.palette.mode === 'light'
                            ? 'rgba(37, 99, 235, 0.06)'
                            : 'rgba(37, 99, 235, 0.12)'
                      }),
                      '&:nth-of-type(even):not(.Mui-selected)': {
                        bgcolor: isNew
                          ? undefined
                          : (theme) =>
                              theme.palette.mode === 'light'
                                ? 'rgba(15, 23, 42, 0.02)'
                                : 'action.hover'
                      },
                      '&.Mui-selected': {
                        bgcolor: (theme) =>
                          theme.palette.mode === 'light'
                            ? 'rgba(37, 99, 235, 0.1)'
                            : 'rgba(37, 99, 235, 0.2)',
                        '&:hover': {
                          bgcolor: (theme) =>
                            theme.palette.mode === 'light'
                              ? 'rgba(37, 99, 235, 0.14)'
                              : 'rgba(37, 99, 235, 0.24)'
                        }
                      }
                    }}
                  >
                    <TableCell padding="checkbox" sx={{ py: 1 }} onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        size="small"
                        checked={isSelected}
                        onChange={() => {
                          if (isSelected) {
                            setEditingRecord(null);
                          } else {
                            setEditingRecord(item);
                          }
                        }}
                        inputProps={{ 'aria-label': `Select ${item.dell_service_tag}` }}
                      />
                    </TableCell>
                    <TableCell sx={{ py: 1, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                      {item.dell_service_tag}
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <Chip
                        size="small"
                        label={(item.status || 'Unknown').toUpperCase()}
                        sx={{
                          height: 26,
                          fontWeight: 600,
                          fontSize: '0.7rem',
                          letterSpacing: '0.02em',
                          bgcolor: statusColors.background,
                          color: statusColors.text,
                          border: 'none',
                          '& .MuiChip-label': { px: 1.25 }
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ py: 1, color: 'text.secondary', maxWidth: 160 }}>{item.customer_name || '—'}</TableCell>
                    <TableCell
                      sx={{
                        py: 1,
                        color: item.customer_address ? 'secondary.main' : 'text.disabled',
                        cursor: item.customer_address ? 'pointer' : 'default',
                        maxWidth: 220,
                        '&:hover': item.customer_address
                          ? { textDecoration: 'underline', textUnderlineOffset: 2 }
                          : undefined
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (item.customer_address) {
                          const url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(item.customer_address)}`;
                          window.open(url, '_blank');
                        }
                      }}
                    >
                      {item.customer_address || '—'}
                    </TableCell>
                    <TableCell
                      sx={{
                        py: 1,
                        color: item.ship_date ? 'primary.main' : 'text.disabled',
                        cursor: item.ship_date ? 'pointer' : 'default',
                        fontWeight: item.ship_date ? 600 : 400,
                        fontVariantNumeric: 'tabular-nums',
                        whiteSpace: 'nowrap',
                        '&:hover': item.ship_date
                          ? { textDecoration: 'underline', textUnderlineOffset: 2 }
                          : undefined
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedTrackingRecord(item);
                        setEditTrackingNumber(item.tracking_number || '');
                        setEditTrackingUrl(item.tracking_url || '');
                        setShowTrackingModal(true);
                      }}
                    >
                      {item.ship_date ? item.ship_date.split('-').reverse().join('-') : '—'}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog
        open={Boolean(detailRecord)}
        onClose={() => setDetailRecord(null)}
        maxWidth="sm"
        fullWidth
        scroll="paper"
        aria-labelledby="device-detail-title"
      >
        {detailRecord && (
          <>
            <DialogTitle id="device-detail-title">Device · {detailRecord.dell_service_tag}</DialogTitle>
            <DialogContent dividers>
              <Stack spacing={2.5} sx={{ pt: 0.5 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    Software type
                  </Typography>
                  {detailRecord.software_type ? (
                    <Chip
                      label={detailRecord.software_type}
                      size="small"
                      variant="outlined"
                      sx={{ height: 28, fontWeight: 600, borderColor: 'divider' }}
                    />
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      —
                    </Typography>
                  )}
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    Windows license key
                  </Typography>
                  <Stack direction="row" alignItems="flex-start" spacing={1} flexWrap="wrap" useFlexGap>
                    <Typography
                      variant="body2"
                      sx={{ wordBreak: 'break-all', flex: '1 1 200px', fontFamily: 'ui-monospace, monospace' }}
                    >
                      {detailRecord.windows_license_key || '—'}
                    </Typography>
                    {detailRecord.windows_license_key ? (
                      <Button
                        size="small"
                        variant="outlined"
                        type="button"
                        onClick={() => {
                          navigator.clipboard.writeText(detailRecord.windows_license_key);
                          toast.success('License key copied to clipboard!');
                        }}
                      >
                        Copy
                      </Button>
                    ) : null}
                  </Stack>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    Status
                  </Typography>
                  <Chip
                    size="small"
                    label={(detailRecord.status || 'Unknown').toUpperCase()}
                    sx={{
                      height: 28,
                      fontWeight: 600,
                      fontSize: '0.7rem',
                      letterSpacing: '0.02em',
                      bgcolor: getStatusColor(detailRecord.status).background,
                      color: getStatusColor(detailRecord.status).text,
                      border: 'none'
                    }}
                  />
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    Customer
                  </Typography>
                  <Typography variant="body2">{detailRecord.customer_name || '—'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    Address
                  </Typography>
                  {detailRecord.customer_address ? (
                    <Link
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(detailRecord.customer_address)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="body2"
                      underline="hover"
                    >
                      {detailRecord.customer_address}
                    </Link>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      —
                    </Typography>
                  )}
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    Ship date
                  </Typography>
                  <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    {detailRecord.ship_date
                      ? detailRecord.ship_date.split('-').reverse().join('-')
                      : '—'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    Tracking
                  </Typography>
                  <Typography variant="body2">{detailRecord.tracking_number || '—'}</Typography>
                  {detailRecord.tracking_url ? (
                    <Link
                      href={detailRecord.tracking_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="body2"
                      underline="hover"
                      sx={{ display: 'inline-block', mt: 0.5, wordBreak: 'break-all' }}
                    >
                      {detailRecord.tracking_url}
                    </Link>
                  ) : null}
                </Box>
              </Stack>
            </DialogContent>
            <DialogActions sx={{ flexWrap: 'wrap', gap: 1, px: 3, py: 2 }}>
              <Button type="button" onClick={() => setDetailRecord(null)}>
                Close
              </Button>
              <Button
                type="button"
                variant="outlined"
                onClick={() => {
                  setEditingRecord(detailRecord);
                  setDetailRecord(null);
                }}
              >
                Edit in form
              </Button>
              <Button
                type="button"
                variant="contained"
                onClick={() => {
                  setSelectedTrackingRecord(detailRecord);
                  setEditTrackingNumber(detailRecord.tracking_number || '');
                  setEditTrackingUrl(detailRecord.tracking_url || '');
                  setIsEditingTracking(false);
                  setShowTrackingModal(true);
                  setDetailRecord(null);
                }}
              >
                Tracking
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Dialog open={showReportModal} onClose={() => setShowReportModal(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Select report period</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              {...fieldProps}
              type="number"
              label="Month (1–12)"
              inputProps={{ min: 1, max: 12 }}
              value={reportMonth}
              onChange={(e) => setReportMonth(e.target.value)}
            />
            <TextField
              {...fieldProps}
              type="number"
              label="Year"
              value={reportYear}
              onChange={(e) => setReportYear(e.target.value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowReportModal(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              setShowReportModal(false);
              generatePDFReport(reportMonth, reportYear);
            }}
          >
            Generate
          </Button>
        </DialogActions>
      </Dialog>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1.5
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" component="span">Show:</Typography>
          <TextField
            select
            size="small"
            value={itemsPerPage}
            onChange={(e) => {
              setItemsPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
            sx={{ minWidth: 88 }}
          >
            <MenuItem value={25}>25</MenuItem>
            <MenuItem value={50}>50</MenuItem>
          </TextField>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="body2" component="span">
            Page: {currentPage} of {Math.max(1, Math.ceil(filteredSortedInventory.length / itemsPerPage))}
          </Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
            disabled={currentPage === 1}
          >
            Prev
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              setCurrentPage((p) =>
                Math.min(
                  p + 1,
                  Math.max(1, Math.ceil(filteredSortedInventory.length / itemsPerPage))
                )
              );
            }}
            disabled={currentPage >= Math.ceil(filteredSortedInventory.length / itemsPerPage) || filteredSortedInventory.length === 0}
          >
            Next
          </Button>
        </Box>
      </Box>

      {/* US Map showing shipped device counts */}
      <Paper sx={{ p: 2, mt: 2, textAlign: 'center' }}>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
          Shipped devices by state
        </Typography>
        <ComposableMap projection="geoAlbersUsa" width={800} height={500}>
          <Geographies geography="https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json">
            {({ geographies }) =>
              geographies.map(geo => (
                <Geography
                  key={geo.rsmKey}
                  geography={geo}
                  fill="#EEE"
                  stroke="#FFF"
                  style={{
                    outline: 'none',
                    boxShadow: 'none',
                    strokeWidth: 1,
                    cursor: 'default'
                  }}
                  onFocus={(e) => e.target.blur()}
                  tabIndex={-1}
                />
              ))
            }
          </Geographies>
          {/* State centers for positioning labels */}
          {(() => {
            const stateCenters = {
              AL: [-86.9023, 32.3182], AK: [-152.4044, 61.3707], AZ: [-111.0937, 34.0489],
              AR: [-92.3731, 35.2010], CA: [-119.4179, 36.7783], CO: [-105.7821, 39.5501],
              CT: [-72.7554, 41.6032], DE: [-75.5277, 38.9108], FL: [-81.5158, 27.6648],
              GA: [-82.9001, 32.1656], HI: [-155.5828, 19.8968], ID: [-114.7420, 44.0682],
              IL: [-89.3985, 40.6331], IN: [-86.1349, 40.5512], IA: [-93.0977, 41.8780],
              KS: [-98.4842, 39.0119], KY: [-84.2700, 37.8393], LA: [-91.9623, 30.9843],
              ME: [-69.4455, 45.2538], MD: [-76.6413, 39.0458], MA: [-71.3824, 42.4072],
              MI: [-85.6024, 44.3148], MN: [-94.6859, 46.7296], MS: [-89.3985, 32.3547],
              MO: [-91.8318, 37.9643], MT: [-110.3626, 46.8797], NE: [-99.9018, 41.4925],
              NV: [-116.4194, 38.8026], NH: [-71.5724, 43.1939], NJ: [-74.4057, 40.0583],
              NM: [-105.8701, 34.5199], NY: [-74.2179, 43.2994], NC: [-79.0193, 35.7596],
              ND: [-100.4370, 47.5515], OH: [-82.9071, 40.4173], OK: [-97.0929, 35.0078],
              OR: [-120.5542, 43.8041], PA: [-77.1945, 41.2033], RI: [-71.4774, 41.5801],
              SC: [-81.1637, 33.8361], SD: [-99.9018, 43.9695], TN: [-86.5804, 35.5175],
              TX: [-99.9018, 31.9686], UT: [-111.0937, 39.3200], VT: [-72.5778, 44.5588],
              VA: [-78.6569, 37.4316], WA: [-120.7401, 47.7511], WV: [-80.4549, 38.5976],
              WI: [-88.7879, 43.7844], WY: [-107.2903, 43.0759]
            };

            // Gather counts and customer names by state
            const stateData = inventory
              .filter(item => item.status === 'Shipped' && item.customer_address)
              .reduce((acc, item) => {
                const stateNameMap = {
                  'Alabama': 'AL','Alaska': 'AK','Arizona': 'AZ','Arkansas': 'AR','California': 'CA','Colorado': 'CO',
                  'Connecticut': 'CT','Delaware': 'DE','Florida': 'FL','Georgia': 'GA','Hawaii': 'HI','Idaho': 'ID',
                  'Illinois': 'IL','Indiana': 'IN','Iowa': 'IA','Kansas': 'KS','Kentucky': 'KY','Louisiana': 'LA',
                  'Maine': 'ME','Maryland': 'MD','Massachusetts': 'MA','Michigan': 'MI','Minnesota': 'MN','Mississippi': 'MS',
                  'Missouri': 'MO','Montana': 'MT','Nebraska': 'NE','Nevada': 'NV','New Hampshire': 'NH','New Jersey': 'NJ',
                  'New Mexico': 'NM','New York': 'NY','North Carolina': 'NC','North Dakota': 'ND','Ohio': 'OH','Oklahoma': 'OK',
                  'Oregon': 'OR','Pennsylvania': 'PA','Rhode Island': 'RI','South Carolina': 'SC','South Dakota': 'SD',
                  'Tennessee': 'TN','Texas': 'TX','Utah': 'UT','Vermont': 'VT','Virginia': 'VA','Washington': 'WA',
                  'West Virginia': 'WV','Wisconsin': 'WI','Wyoming': 'WY'
                };
                let state = 'Unknown';
                for (const [name, code] of Object.entries(stateNameMap)) {
                  const regexFull = new RegExp(`\\b${name}\\b`, 'i');
                  if (regexFull.test(item.customer_address)) {
                    state = code;
                    break;
                  }
                }
                if (state === 'Unknown') {
                  const abbrMatch = item.customer_address.match(/(?:,|\s|^)([A-Z]{2})(?:\s|,|$)/);
                  if (
                    abbrMatch &&
                    stateNameMap[Object.keys(stateNameMap).find(key => stateNameMap[key] === abbrMatch[1])]
                  ) {
                    state = abbrMatch[1];
                  }
                }
                if (!acc[state]) acc[state] = { count: 0, customers: [] };
                acc[state].count += 1;
                if (item.customer_name) acc[state].customers.push(item.customer_name);
                return acc;
              }, {});
            const countsByState = Object.entries(stateData);

            const maxCount = Math.max(...countsByState.map(([_, obj]) => obj.count), 1);

            return countsByState.map(([state, obj], idx) => {
              const coords = stateCenters[state];
              if (!coords) return null;
              const fontSize = 8 + (obj.count / maxCount) * 10; // scale font size
              // Find full state name for tooltip
              const stateNameMap = {
                'Alabama': 'AL','Alaska': 'AK','Arizona': 'AZ','Arkansas': 'AR','California': 'CA','Colorado': 'CO',
                'Connecticut': 'CT','Delaware': 'DE','Florida': 'FL','Georgia': 'GA','Hawaii': 'HI','Idaho': 'ID',
                'Illinois': 'IL','Indiana': 'IN','Iowa': 'IA','Kansas': 'KS','Kentucky': 'KY','Louisiana': 'LA',
                'Maine': 'ME','Maryland': 'MD','Massachusetts': 'MA','Michigan': 'MI','Minnesota': 'MN','Mississippi': 'MS',
                'Missouri': 'MO','Montana': 'MT','Nebraska': 'NE','Nevada': 'NV','New Hampshire': 'NH','New Jersey': 'NJ',
                'New Mexico': 'NM','New York': 'NY','North Carolina': 'NC','North Dakota': 'ND','Ohio': 'OH','Oklahoma': 'OK',
                'Oregon': 'OR','Pennsylvania': 'PA','Rhode Island': 'RI','South Carolina': 'SC','South Dakota': 'SD',
                'Tennessee': 'TN','Texas': 'TX','Utah': 'UT','Vermont': 'VT','Virginia': 'VA','Washington': 'WA',
                'West Virginia': 'WV','Wisconsin': 'WI','Wyoming': 'WY'
              };
              return (
                <Marker key={idx} coordinates={coords}>
                  <g
                    onMouseEnter={(e) => {
                      const tooltip = document.getElementById('state-tooltip');
                      if (tooltip) {
                        tooltip.style.display = 'block';
                        const fullName = Object.keys(stateNameMap).find(key => stateNameMap[key] === state) || state;
                        const customerList = stateData[state].customers.map(name => `<div style="text-align:left;">${name}</div>`).join('');
                        tooltip.innerHTML = `<strong>${fullName}</strong><br/>${customerList}`;
                        tooltip.style.left = `${e.clientX + 10}px`;
                        tooltip.style.top = `${e.clientY + 10}px`;
                      }
                    }}
                    onMouseMove={(e) => {
                      const tooltip = document.getElementById('state-tooltip');
                      if (tooltip) {
                        tooltip.style.left = `${e.clientX + 10}px`;
                        tooltip.style.top = `${e.clientY + 10}px`;
                      }
                    }}
                    onMouseLeave={() => {
                      const tooltip = document.getElementById('state-tooltip');
                      if (tooltip) tooltip.style.display = 'none';
                    }}
                  >
                    <text textAnchor="middle" fill="#F00" fontSize={fontSize} style={{ pointerEvents: 'all' }}>
                      {obj.count}
                    </text>
                  </g>
                </Marker>
              );
            });
          })()}
        </ComposableMap>
        <div
          id="state-tooltip"
          style={{
            position: 'fixed',
            display: 'none',
            backgroundColor: 'rgba(0,0,0,0.8)',
            color: '#fff',
            padding: '6px 10px',
            borderRadius: '4px',
            fontSize: '12px',
            pointerEvents: 'none',
            zIndex: 9999
          }}
        />
      </Paper>

      <Dialog
        open={showTrackingModal && !!selectedTrackingRecord}
        onClose={() => {
          setShowTrackingModal(false);
          setIsEditingTracking(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        {selectedTrackingRecord && (
          <>
            <DialogTitle sx={{ color: 'text.secondary' }}>
              Tracking info for {selectedTrackingRecord.dell_service_tag}
            </DialogTitle>
            <DialogContent>
              <Stack spacing={2} sx={{ pt: 1 }}>
                <Box>
                  <Typography variant="subtitle2" gutterBottom>Tracking number</Typography>
                  {isEditingTracking ? (
                    <TextField
                      {...fieldProps}
                      value={editTrackingNumber}
                      onChange={(e) => setEditTrackingNumber(e.target.value)}
                    />
                  ) : (
                    <Typography variant="body2">{selectedTrackingRecord.tracking_number || 'None set'}</Typography>
                  )}
                </Box>
                <Box>
                  <Typography variant="subtitle2" gutterBottom>Tracking URL</Typography>
                  {isEditingTracking ? (
                    <TextField
                      {...fieldProps}
                      value={editTrackingUrl}
                      onChange={(e) => setEditTrackingUrl(e.target.value)}
                    />
                  ) : selectedTrackingRecord.tracking_url ? (
                    <Typography
                      component="a"
                      variant="body2"
                      href={selectedTrackingRecord.tracking_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ wordBreak: 'break-all' }}
                    >
                      {selectedTrackingRecord.tracking_url}
                    </Typography>
                  ) : (
                    <Typography variant="body2">None set</Typography>
                  )}
                </Box>
              </Stack>
            </DialogContent>
            <DialogActions>
              {isEditingTracking ? (
                <>
                  <Button onClick={() => setIsEditingTracking(false)}>Cancel</Button>
                  <Button
                    variant="contained"
                    onClick={async () => {
                      const { error } = await supabase
                        .from('build_inventory')
                        .update({
                          tracking_number: editTrackingNumber,
                          tracking_url: editTrackingUrl
                        })
                        .eq('dell_service_tag', selectedTrackingRecord.dell_service_tag);

                      if (error) {
                        toast.error('Failed to save tracking info: ' + error.message);
                      } else {
                        toast.success('Tracking info updated!');
                        setShowTrackingModal(false);
                        setIsEditingTracking(false);
                        fetchInventory();
                      }
                    }}
                  >
                    Save
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    onClick={() => {
                      setShowTrackingModal(false);
                      setIsEditingTracking(false);
                    }}
                  >
                    Close
                  </Button>
                  <Button
                    variant="contained"
                    onClick={() => {
                      setEditTrackingNumber(selectedTrackingRecord.tracking_number || '');
                      setEditTrackingUrl(selectedTrackingRecord.tracking_url || '');
                      setIsEditingTracking(true);
                    }}
                  >
                    Edit
                  </Button>
                </>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>
    </Stack>
  );
}


export default BuildInventory;
